import { Injectable } from '@angular/core';
import { NgForage } from 'ngforage';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { NgForageTimestampService } from '../../ng-forage/ng-forage-timestamp.service';
import { AbstractOfflineStorageAsyncTimestampService } from '../abstract/abstract-offline-storage-async-timestamp.service';
import { ExpiredImmoIPSubProjectChangesRemover } from './expired-immo-ip-sub-project-changes-remover';
import { ImmoIPSubProjectChangesObject } from './immo-ip-sub-project-changes-object';
import { cloneDeep } from 'lodash';
import * as moment from 'moment-timezone';
import DateHelper from 'src/app/helpers/date-helper';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import AssetHelper from 'src/app/helpers/asset-helper';
import { OfflineAsset } from '../../placer-offline-changes/offline-asset';

@Injectable({
  providedIn: 'root',
})
export class ImmoIPSubProjectChangesService extends AbstractOfflineStorageAsyncTimestampService<
  number,
  ImmoIPSubProjectChangesObject,
  ExpiredImmoIPSubProjectChangesRemover
> {
  constructor(
    protected ngForage: NgForage,
    protected ngForageTimestampService: NgForageTimestampService
  ) {
    super(
      'immo-ip-sub-project-change-',
      'immo-ip-sub-project-changes',
      ngForage,
      ngForageTimestampService
    );
  }

  protected instantiateRemover(): ExpiredImmoIPSubProjectChangesRemover {
    return new ExpiredImmoIPSubProjectChangesRemover(
      this,
      this.ngForage,
      this.ngForageTimestampService
    );
  }

  // Override
  protected processRetrievedObject(
    object: ImmoIPSubProjectChangesObject
  ): ImmoIPSubProjectChangesObject {
    if (
      object != null &&
      CollectionHelper.isPopulated(object.registeredActions)
    ) {
      object.registeredActions.forEach(
        (r) =>
          (r.actualDate =
            r.date == null
              ? null
              : moment.tz(r.date, DateHelper.DEFAULT_TIME_ZONE))
      );
    }
    return object;
  }

  // Override
  protected verifyToStoreObject(object: ImmoIPSubProjectChangesObject): void {
    if (object == null || object.id == null) {
      throw new Error(
        'immoIPSubProjectChangesObject must not be null and id must not be null'
      );
    }
  }

  // Override
  protected preprocessToStoreObject(
    object: ImmoIPSubProjectChangesObject
  ): ImmoIPSubProjectChangesObject {
    const toReturn = <ImmoIPSubProjectChangesObject>cloneDeep(object);
    if (CollectionHelper.isPopulated(toReturn.registeredActions)) {
      toReturn.registeredActions.forEach((r) => delete r.actualDate); // IndexedDB cannot store functions, which is a problem for Moments
    }
    return toReturn;
  }

  public convertToID(o: any): number {
    const toReturn = GeneralHelper.isNumeric(o) ? o : Number(o);
    if (!GeneralHelper.isNumeric(toReturn)) {
      throw new Error(`${o} could not be converted to number`);
    }
    return toReturn;
  }

  protected stringifyID(id: number): string {
    return id.toString();
  }

  // Override
  public async cleanupOfflineObject(
    object: ImmoIPSubProjectChangesObject
  ): Promise<void> {
    if (object == null) {
      return;
    }
    if (CollectionHelper.isPopulated(object.offlineAssets)) {
      await Promise.all(
        object.offlineAssets.map((offlineAsset) =>
          this.ngForage.removeItem(
            AssetHelper.getStorageKeyFor(offlineAsset.localFileUuid)
          )
        )
      );
    }
    if (object.notExecutedHistoryItemObject != null) {
      await this.cleanupNotExecutedHistoryItemAssets(
        object.notExecutedHistoryItemObject.offlineAssets
      );
    }
  }

  public async cleanupNotExecutedHistoryItemAssets(
    notExecutedHistoryItemAssets: OfflineAsset[]
  ): Promise<void> {
    if (CollectionHelper.isPopulated(notExecutedHistoryItemAssets)) {
      for (const asset of notExecutedHistoryItemAssets) {
        await this.ngForage.removeItem(
          AssetHelper.getStorageKeyFor(asset.localFileUuid)
        );
      }
    }
  }
}
